import React, { useState, useCallback, useEffect } from "react";
import {
  MessageSquare,
  Plus,
  Trash2,
  Sparkles,
  ArrowUp,
  ArrowDown,
  PhoneOff,
  Sliders,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  ShieldAlert,
  HelpCircle,
  GitFork,
  FilePlus
} from "lucide-react";
import toast from "react-hot-toast";
import Button from "../../Button/Button.js";
import Input from "../../Input/Input.js";
import Modal from "../../Modal/Modal.js";
import AuthService from "../../../services/Auth.service.js";
import { parseVariables, SECTION_META } from "./scriptFlowUtils.js";

const STEPS = [
  { id: "welcome_message", num: 1, label: "Greeting", sub: "Opening message", color: "blue", icon: MessageSquare, hint: "First thing the agent says when customer picks up", required: true },
  { id: "mandatory_disclosures", num: 2, label: "Mandatory", sub: "Compliance & Disclosures", color: "amber", icon: ShieldAlert, hint: "Regulatory compliance, disclaimers & mandatory disclosures" },
  { id: "scenarios", num: 3, label: "Scenarios", sub: "Dynamic use-cases", color: "purple", icon: GitFork, hint: "Dynamic details & extra use-case scenarios" },
  { id: "rebuttals_faqs", num: 4, label: "Rebuttals / FAQs", sub: "Objection handling", color: "indigo", icon: HelpCircle, hint: "Answer common questions and handle objections" },
  { id: "final_call_message", num: 5, label: "Closing", sub: "Call to Action", color: "rose", icon: PhoneOff, hint: "Call to Action, confirm next steps & polite sign-off", required: true },
  { id: "more_info", num: 6, label: "More Info", sub: "Extra resources", color: "emerald", icon: FilePlus, hint: "Extra resources, documentation links & follow-up data" }
];

function StepEditor({ section, sectionId, generatingId, error, onUpdate, onGenerate, onOpenVariables }) {
  const vars = parseVariables(section?.prompt || "");

  return (
    <div className="esb-step-editor">
      <div className="esb-editor-toolbar">
        <Button variant="ghost" size="sm" onClick={() => onOpenVariables?.(sectionId, section?.title || SECTION_META[sectionId]?.title)} title="Configure variables for this step">
          <Sliders size={13} /> Variables
        </Button>
        <Button
          variant="outline" size="sm"
          onClick={() => onGenerate(sectionId, section?.title || SECTION_META[sectionId]?.title)}
          disabled={generatingId === sectionId}
          title="Generate script with AI"
        >
          <Sparkles size={13} /> {generatingId === sectionId ? "Generating..." : "Generate AI"}
        </Button>
      </div>

      <textarea
        className="esb-textarea"
        style={error ? { borderColor: "#ef4444", boxShadow: "0 0 0 1px #ef4444" } : {}}
        rows={5}
        value={section?.prompt || ""}
        onChange={(e) => onUpdate(sectionId, "prompt", e.target.value)}
        placeholder={SECTION_META[sectionId]?.placeholder || "Write the agent script for this step..."}
        spellCheck={false}
      />

      {error && (
        <div style={{ color: "#ef4444", fontSize: "12px", fontWeight: 600, marginTop: "6px" }}>
          {error}
        </div>
      )}

      {vars.length > 0 && (
        <div className="esb-active-vars">
          {vars.map((v) => <span key={v} className="esb-active-badge">{`{${v}}`}</span>)}
        </div>
      )}
    </div>
  );
}

function DynamicStepList({
  items,
  category,
  emptyText,
  addBtnText,
  onAdd,
  focusItemId,
  onUpdate,
  onDelete,
  onMove,
  onGenerate,
  onOpenVariables,
  generatingId
}) {
  return (
    <div className="esb-dynamic-list-wrap">
      {items.length === 0 ? (
        <div className="esb-empty">{emptyText}</div>
      ) : (
        <div className="esb-steps-list">
          {items.map((step, idx) => {
            const stepVars = parseVariables(step.prompt || "");
            return (
              <div key={step.id} className="esb-step-row">
                <div className="esb-step-row__head">
                  <span className="esb-step-row__num">{idx + 1}</span>
                  <input
                    className="esb-step-row__title"
                    value={step.title || ""}
                    onChange={(e) => onUpdate(step.id, "title", e.target.value)}
                    placeholder="Title..."
                    autoFocus={step.id === focusItemId}
                  />
                  <div className="esb-step-row__actions">
                    <Button variant="ghost" size="sm" onClick={() => onOpenVariables?.(step.id, step.title)} title="Configure variables"><Sliders size={12} /></Button>
                    <Button variant="ghost" size="sm" onClick={() => onGenerate(step.id, step.title)} disabled={generatingId === step.id} title="Generate AI"><Sparkles size={12} /></Button>
                    <Button variant="ghost" size="sm" onClick={() => onMove(items, idx, -1)} disabled={idx === 0} title="Move Up"><ArrowUp size={13} /></Button>
                    <Button variant="ghost" size="sm" onClick={() => onMove(items, idx, 1)} disabled={idx === items.length - 1} title="Move Down"><ArrowDown size={13} /></Button>
                    <Button variant="ghost" size="sm" onClick={() => onDelete(step.id)} title="Delete"><Trash2 size={13} /></Button>
                  </div>
                </div>
                <textarea
                  className="esb-textarea esb-textarea--compact"
                  rows={2}
                  value={step.prompt || ""}
                  onChange={(e) => onUpdate(step.id, "prompt", e.target.value)}
                  placeholder="Instructions for this step..."
                  spellCheck={false}
                />
                {stepVars.length > 0 && (
                  <div className="esb-active-vars">
                    {stepVars.map((v) => (
                      <span key={v} className="esb-active-badge">{`{${v}}`}</span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
       <button type="button" className="esb-add-btn" onClick={() => onAdd(category)}>
        <Plus size={14} /> {addBtnText}
      </button>
    </div>
  );
}

export default function EasyScriptBuilder({ sections = [], handleInputChange, selectedAgent, onOpenVariablesForStep, formErrors = {} }) {
  const [activeStep, setActiveStep] = useState(1);
  const [generatingId, setGeneratingId] = useState(null);
  const [addModalCategory, setAddModalCategory] = useState(null); // 'scenario' | 'more_info'
  const [newTitle, setNewTitle] = useState("");
  const [newScenarioId, setNewScenarioId] = useState(null);

  useEffect(() => {
    if (formErrors.welcome_message) {
      setActiveStep(1);
    } else if (formErrors.final_call_message) {
      setActiveStep(5);
    }
  }, [formErrors.welcome_message, formErrors.final_call_message]);

  const knownIds = ["welcome_message", "mandatory_disclosures", "rebuttals_faqs", "final_call_message"];

  const getSection = (id) => sections.find((s) => s.id === id) || { id, title: SECTION_META[id]?.title || "", prompt: "" };

  // Filter dynamic lists
  const scenarioSteps = sections.filter((s) => s.category === "scenario" || (!s.category && !knownIds.includes(s.id)));
  const moreInfoSteps = sections.filter((s) => s.category === "more_info");

  const commitSections = useCallback((updated) => {
    handleInputChange("prompt_sections", updated);
    handleInputChange("user_defined_prompt", updated);
  }, [handleInputChange]);

  const updateField = useCallback((id, field, value) => {
    const updated = sections.map((s) => s.id === id ? { ...s, [field]: value } : s);
    commitSections(updated);
  }, [sections, commitSections]);

  const handleGenerate = useCallback(async (sectionId, title) => {
    setGeneratingId(sectionId);
    const toastId = toast.loading(`Generating "${title || 'step'}"...`);
    try {
      const currentSection = sections.find((s) => s.id === sectionId);
      const existingContent = currentSection?.prompt || "";
      const res = await AuthService.generateWithAI({
      content: existingContent || `Create a professional voice-bot prompt for the "${title || 'step'}" section.`,
      demo_content:"",
      campaign_id: selectedAgent?.id || "new-agent",
      section_id: sectionId
      });
      const generated = res?.data?.content || res?.data?.script || res?.data?.data?.script || res?.data;
      if (typeof generated === "string" && generated.trim()) {
        updateField(sectionId, "prompt", generated);
        toast.success(`"${title || 'Step'}" generated`, { id: toastId });
      } else {
        toast.error("AI returned empty content", { id: toastId });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Generation failed", { id: toastId });
    } finally { setGeneratingId(null); }
  }, [selectedAgent, updateField]);

  const addDynamicItem = () => {
    if (!newTitle.trim()) { toast.error("Enter a title"); return; }
    const cat = addModalCategory || "scenario";
    const id = `${cat}_${Date.now()}`;
    commitSections([...sections, {
      id,
      category: cat,
      title: newTitle.trim(),
      prompt: "",
      description: cat === "scenario" ? "Dynamic Scenario" : "Extra Resource"
    }]);
    setNewTitle("");
    setAddModalCategory(null);
    toast.success(cat === "scenario" ? "Scenario added" : "Resource added");
  };

  const deleteDynamicItem = (id) => {
    commitSections(sections.filter((s) => s.id !== id));
    toast.success("Item removed");
  };

  const moveDynamicItem = (list, index, dir) => {
    const globalIdx = sections.findIndex((s) => s.id === list[index]?.id);
    if (globalIdx < 0) return;
    const newIdx = globalIdx + dir;
    if (newIdx < 0 || newIdx >= sections.length) return;
    const arr = [...sections];
    const [moved] = arr.splice(globalIdx, 1);
    arr.splice(newIdx, 0, moved);
    commitSections(arr);
  };

  const currentStepConfig = STEPS[activeStep - 1];
  const addBlankScenario = () => {
  const id = `scenario_${Date.now()}_${Math.random()
    .toString(36)
    .slice(2, 8)}`;

  const newScenario = {
    id,
    category: "scenario",
    title: "",
    prompt: "",
    description: "Dynamic Scenario"
  };

  setNewScenarioId(id);
  commitSections([...sections, newScenario]);
  toast.success("Scenario added");
};


  return (
    <div className="esb esb--vertical">
      {/* ─── Left Vertical Stepper Navigation ─── */}
      <nav className="esb-vstepper">
        <div className="esb-vstepper__head">
          <span className="esb-vstepper__title">Script Flow Steps</span>
        </div>
        <div className="esb-vstepper__list">
          {STEPS.map((step) => {
            let count = 0;
            if (step.id === "scenarios") count = scenarioSteps.length;
            if (step.id === "more_info") count = moreInfoSteps.length;

            return (
              <button
                key={step.num}
                type="button"
                className={`esb-vstepper__item ${activeStep === step.num ? "active" : ""} ${activeStep > step.num ? "done" : ""}`}
                onClick={() => setActiveStep(step.num)}
              >
                <span className={`esb-vstepper__circle ${step.color}`}>{step.num}</span>
                <div className="esb-vstepper__meta">
                  <span className="esb-vstepper__label">
                    {step.label}
                    {step.required && <span style={{ color: "#ef4444", marginLeft: "2px" }}>*</span>}
                  </span>
                  <span className="esb-vstepper__sub">{step.sub}</span>
                </div>
                {count > 0 && <span className="esb-vstepper__count">{count}</span>}
              </button>
            );
          })}
        </div>
      </nav>

      <div className="esb-vcontent">
        <div className="esb-card">
          <div className={`esb-card__header ${currentStepConfig.color}`}>
            <div className="esb-card__header-left">
              <span className={`esb-badge ${currentStepConfig.color}`}>Step {activeStep} of 6</span>
              {React.createElement(currentStepConfig.icon, { size: 16 })}
              <h3>
                {currentStepConfig.id === "scenarios" 
                  ? `Scenarios (${scenarioSteps.length})` 
                  : currentStepConfig.id === "more_info" 
                  ? `More Info (${moreInfoSteps.length})` 
                  : SECTION_META[currentStepConfig.id]?.title || currentStepConfig.label}
                {currentStepConfig.required && <span style={{ color: "#ef4444", marginLeft: "2px" }}>*</span>}
              </h3>
            </div>
            <span className="esb-card__hint">{currentStepConfig.hint}</span>
          </div>

          <div className="esb-card__body">
            {/* Step 1: Greeting */}
            {activeStep === 1 && (
              <StepEditor
                section={getSection("welcome_message")}
                sectionId="welcome_message"
                generatingId={generatingId}
                error={formErrors.welcome_message}
                onUpdate={updateField}
                onGenerate={handleGenerate}
                onOpenVariables={onOpenVariablesForStep}
              />
            )}

            {/* Step 2: Mandatory Disclosures */}
            {activeStep === 2 && (
              <StepEditor
                section={getSection("mandatory_disclosures")}
                sectionId="mandatory_disclosures"
                generatingId={generatingId}
                onUpdate={updateField}
                onGenerate={handleGenerate}
                onOpenVariables={onOpenVariablesForStep}
              />
            )}

            {/* Step 3: Scenarios ++ */}
            {activeStep === 3 && (
              <DynamicStepList
                  items={scenarioSteps}
                  category="scenario"
                  emptyText="No dynamic scenarios added yet. Add one below."
                  addBtnText="Add Scenario"
                  onAdd={addBlankScenario}
                  focusItemId={newScenarioId}
                  onUpdate={updateField}
                  onDelete={deleteDynamicItem}
                  onMove={moveDynamicItem}
                  onGenerate={handleGenerate}
                  onOpenVariables={onOpenVariablesForStep}
                  generatingId={generatingId}
                />
            )}

            {/* Step 4: Rebuttals / FAQs */}
            {activeStep === 4 && (
              <StepEditor
                section={getSection("rebuttals_faqs")}
                sectionId="rebuttals_faqs"
                generatingId={generatingId}
                onUpdate={updateField}
                onGenerate={handleGenerate}
                onOpenVariables={onOpenVariablesForStep}
              />
            )}

            {/* Step 5: Closing */}
            {activeStep === 5 && (
              <StepEditor
                section={getSection("final_call_message")}
                sectionId="final_call_message"
                generatingId={generatingId}
                error={formErrors.final_call_message}
                onUpdate={updateField}
                onGenerate={handleGenerate}
                onOpenVariables={onOpenVariablesForStep}
              />
            )}

            {/* Step 6: More Info ++ */}
            {activeStep === 6 && (
              <DynamicStepList
                items={moreInfoSteps}
                category="more_info"
                emptyText="No extra info resources added yet. Add one below."
                addBtnText="Add Resource / Info"
                onAdd={(cat) => setAddModalCategory(cat)}
                onUpdate={updateField}
                onDelete={deleteDynamicItem}
                onMove={moveDynamicItem}
                onGenerate={handleGenerate}
                onOpenVariables={onOpenVariablesForStep}
                generatingId={generatingId}
              />
            )}
          </div>

          {/* Card Navigation Footer */}
          <div className="esb-card__footer">
            {activeStep > 1 ? (
              <Button variant="outline" size="sm" onClick={() => setActiveStep((s) => s - 1)}>
                <ChevronLeft size={14} /> Back
              </Button>
            ) : <div />}
            <span className="esb-card__progress">Step {activeStep} of 6</span>
            {activeStep < 6 ? (
              <Button variant="primary" size="sm" onClick={() => setActiveStep((s) => s + 1)}>
                Next <ChevronRight size={14} />
              </Button>
            ) : (
              <span className="esb-card__complete"><CheckCircle2 size={14} /> All 6 steps configured</span>
            )}
          </div>
        </div>
      </div>

      {/* Add Item Modal */}
      <Modal
        isOpen={Boolean(addModalCategory)}
        onClose={() => setAddModalCategory(null)}
        title={addModalCategory === "scenario" ? "Add Scenario" : "Add Extra Resource"}
        subtitle={addModalCategory === "scenario" ? "Create a new custom use-case scenario" : "Create a new follow-up resource item"}
        maxWidth="420px"
        footer={<>
          <Button variant="ghost" size="sm" onClick={() => setAddModalCategory(null)}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={addDynamicItem}>Add Item</Button>
        </>}
      >
        <Input
          type="text"
          name="new-item-title"
          label={addModalCategory === "scenario" ? "Scenario Title" : "Resource Title"}
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          placeholder={addModalCategory === "scenario" ? "e.g. Loan Refinancing Request" : "e.g. Website Self-service URL"}
          autoFocus
        />
      </Modal>
    </div>
  );
}
